import { computed, ref, watch } from 'vue';
import { transactionsApi } from '@/api/transactions';
import type {
    Transaction,
    PayDeductionType,
    TransactionType,
} from '@/types/models';
import { createSharedComposable } from '@vueuse/core';
import type {
    ExportTransactionsRequest,
    TransactionDetailsRequest,
    TransactionsRequest,
} from '@/types/api/transactions';
import { useGet, usePost, useToaster, useFilters } from '@foodics/ui-common';
import { useLocale } from './locale';
import { resolvePaymentPartyName } from './payments';
import { resolveSadadBillPaymentPartyName } from './sadad';
import { parseError } from '@/utils/errors';
import { isEmpty, omitBy } from 'lodash-es';
import { transactionFlowEnum, transactionTypeEnum } from '@/enums/transactions';
import { usePagination } from './usePagination';

export type TransactionFilters = {
    bank_account_id?: string[];
    date_range?: string;
    min_amount?: string;
    max_amount?: string;
    type?: number[];
};

export const useTransactions = createSharedComposable(() => {
    const { onLocaleChange } = useLocale();
    const transactions = ref<Transaction[]>([]);
    const { setFilter, appliedFilters, resetFilters } =
        useFilters<TransactionFilters>();

    const hasFiltersApplied = computed(() =>
        Object.values(appliedFilters.value).some((value) => !isEmpty(value)),
    );

    const apiParams = computed(() => {
        return {
            filter: isEmpty(appliedFilters.value)
                ? undefined
                : appliedFilters.value,
            include: 'bankAccount.bank,transfer.destination,billPayment.biller',
        };
    });
    const {
        items,
        isLoading,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
    } = usePagination<TransactionsRequest>(transactionsApi.list, {
        fetchOptions: computed(() => ({ params: apiParams.value })),
    });

    watch(
        appliedFilters,
        () => {
            transactions.value = [];
            resetPagination();
        },
        { deep: true },
    );
    onLocaleChange(() => {
        transactions.value = [];
        resetPagination();
    });
    watch(items, () => {
        transactions.value.push(...mapTransaction(items.value));
    });
    const context = {
        fetchNextPage,
        hasNextPage,
        hasFetchedInitialPage,
        setFilter,
        resetFilters,
        isLoading,
        transactions,
        appliedFilters,
        hasFiltersApplied,
    };
    return context;
});

export function useTransaction(transactionId: string) {
    const { data, isLoading, error } = useGet<
        TransactionDetailsRequest['response']
    >(transactionsApi.details(transactionId));

    return {
        transaction: computed(() => data.value?.data),
        isLoading,
        error,
    };
}

const mapTransaction = (data: Transaction[]) => {
    return data.map((transaction) => ({
        ...transaction,
        date: transaction.date,
    }));
};

export function resolveTransactionPartyName(transaction: Transaction) {
    const { t } = useLocale();

    if (isCollectionTransaction(transaction)) {
        return resolveDeductionTypeLabels(getDeductionType(transaction)).title;
    }

    if (transaction.bill_payment) {
        return resolveSadadBillPaymentPartyName(transaction.bill_payment);
    }

    if (transaction.participant) {
        return t('transactions.table.sales');
    }

    // Check enrichment data first (prioritize AI-enriched names)
    if (transaction.enrichment) {
        const name =
            transaction.flow === transactionFlowEnum.INWARD
                ? transaction.enrichment.sender_name
                : transaction.enrichment.receiver_name;
        if (name) return name;
    }

    if (transaction.transfer) {
        return resolvePaymentPartyName(transaction.transfer);
    }

    return t('generic.common_terms.unknown_company');
}

export function resolveCompanyLogo(companyName: string | undefined) {
    const companyImageMap: Record<string, string> = {
        'Foodics Pay': 'foodics_pay.webp',
        'Arabian Factory for Paper': 'arabian_factory_for_paper.png',
        // ...other mappings
    };

    if (!companyName || !companyImageMap[companyName]) return undefined;

    return new URL(
        `/resources/client/assets/images/logos/companies/${companyImageMap[companyName]}`,
        import.meta.url,
    ).href;
}

// This will be handled from BE later on, the BE will return the avatar URL for the participant directly
export function resolveParticipantAvatar(participant: string | null) {
    const participantNameMap: Record<string, string> = {
        'Foodics Pay Settlement': 'foodics_pay.webp',
    };

    if (!participant || !participantNameMap[participant]) return undefined;

    return new URL(
        `/resources/client/assets/images/logos/companies/${participantNameMap[participant]}`,
        import.meta.url,
    ).href;
}

export function useExportTransactions() {
    const { addToast } = useToaster();
    const { t } = useLocale();
    const { appliedFilters } = useTransactions();

    const { execute, isLoading } = usePost<
        ExportTransactionsRequest['payload'],
        ExportTransactionsRequest['response']
    >(transactionsApi.export);

    async function exportTransactions() {
        try {
            const [from, to] =
                appliedFilters.value.date_range?.split(' - ') ?? [];

            const { error: executeError } = await execute(
                omitBy(
                    {
                        from,
                        to,
                        bank_account_ids: appliedFilters.value.bank_account_id,
                        min_amount: appliedFilters.value.min_amount,
                        max_amount: appliedFilters.value.max_amount,
                        type: appliedFilters.value.type,
                    },
                    isEmpty,
                ),
            );

            if (executeError) throw executeError;

            addToast({
                title: t('transactions.export.successMessage'),
                variant: 'success',
            });
        } catch (err) {
            addToast({
                title: parseError(err, t('transactions.export.errorMessage')),
                variant: 'error',
            });
            throw err;
        }
    }

    return {
        exportTransactions,
        isLoading,
    };
}

export function resolveTransactionParticipantLabel(
    participant: NonNullable<Transaction['participant']>,
) {
    const { t } = useLocale();

    const participantLabelMap: Record<
        NonNullable<Transaction['participant']>,
        string
    > = {
        'Foodics Pay Settlement': t(
            'transactions.participants.foodicsPaySettlement',
        ),
        SADAD: t('transactions.participants.SADAD'),
    };

    return participantLabelMap[participant] || participant;
}

export function resolveTransactionTypeLabel(type: TransactionType) {
    const { t } = useLocale();

    const transactionTypeLabelMap: Record<TransactionType, string> = {
        [transactionTypeEnum.SALES]: t('transactions.transactionTypes.sales'),
        [transactionTypeEnum.TRANSFER_IN]: t(
            'transactions.transactionTypes.transferIn',
        ),
        [transactionTypeEnum.TRANSFER_OUT]: t(
            'transactions.transactionTypes.transferOut',
        ),
        [transactionTypeEnum.BILL_PAYMENT]: t(
            'transactions.transactionTypes.billPayment',
        ),
        [transactionTypeEnum.COLLECTION]: t(
            'transactions.transactionTypes.collection',
        ),
    };

    return (
        transactionTypeLabelMap[type] ||
        t('transactions.transactionTypes.unknown')
    );
}

export function isFoodicsPaySettlement(transaction: Transaction) {
    return transaction.participant === 'Foodics Pay Settlement';
}

export function isCollectionTransaction(transaction: Transaction) {
    return transaction.type === transactionTypeEnum.COLLECTION;
}

export function getDeductionType(
    transaction: Transaction,
): PayDeductionType | undefined {
    if (!transaction?.transfer?.meta?.deduction_type) return undefined;
    return transaction.transfer.meta.deduction_type;
}

export function resolveDeductionTypeLabels(
    deductionType: PayDeductionType | undefined,
): {
    title: string;
    subtitle: string;
} {
    const { t } = useLocale();

    if (!deductionType) {
        return {
            title: t('transactions.deductionTypes.unknown.title'),
            subtitle: t('transactions.deductionTypes.unknown.subtitle'),
        };
    }

    const deductionTypeLabelsMap: Record<
        PayDeductionType,
        { title: string; subtitle: string }
    > = {
        'subscription-fees': {
            title: t('transactions.deductionTypes.subscription.title'),
            subtitle: t('transactions.deductionTypes.subscription.subtitle'),
        },
        deductions: {
            title: t('transactions.deductionTypes.deduction.title'),
            subtitle: t('transactions.deductionTypes.deduction.subtitle'),
        },
        'capital-loan-installments': {
            title: t('transactions.deductionTypes.foodicsCapital.title'),
            subtitle: t('transactions.deductionTypes.foodicsCapital.subtitle'),
        },
        'bnpl-installments': {
            title: t('transactions.deductionTypes.rmsSubscription.title'),
            subtitle: t('transactions.deductionTypes.rmsSubscription.subtitle'),
        },
        'supply-installments': {
            title: t('transactions.deductionTypes.supplyLoan.title'),
            subtitle: t('transactions.deductionTypes.supplyLoan.subtitle'),
        },
        'real-time-reversal': {
            title: t('transactions.deductionTypes.realTimeReversal.title'),
            subtitle: t(
                'transactions.deductionTypes.realTimeReversal.subtitle',
            ),
        },
    };

    return deductionTypeLabelsMap[deductionType];
}
